import React, { useState, useEffect } from 'react';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { 
    Users, Clock, TrendingUp, Filter, Loader2
} from 'lucide-react';
import api from '../services/api'; 

const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00:00';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const RelatoriosScreen = () => { 
    const getLocalYYYYMMDD = (d) => {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    
    // Filtros
    const [startDate, setStartDate] = useState(getLocalYYYYMMDD(firstDay));
    const [endDate, setEndDate] = useState(getLocalYYYYMMDD(today));
    const [selectedAgent, setSelectedAgent] = useState(''); 
    
    // Dados (Inicialização Segura)
    const [reportData, setReportData] = useState([]);
    const [metrics, setMetrics] = useState({
        total: 0, ended: 0, canceled: 0, avgTime: 0, agentsArray: []
    });
    const [loading, setLoading] = useState(false);
    const [agentsList, setAgentsList] = useState([]);

    // 1. Carregar lista de agentes
    useEffect(() => {
        const loadAgents = async () => {
            try {
                const res = await api.get('/users'); 
                setAgentsList(Array.isArray(res.data) ? res.data : []);
            } catch (e) { console.error(e); }
        };
        loadAgents();
    }, []);

    // 2. Buscar Relatório
    useEffect(() => {
        const fetchReportData = async () => {
            setLoading(true);
            try {
                const response = await api.get('/reports/team', {
                    params: { startDate, endDate, agentId: selectedAgent }
                });
                
                // 🚨 BLINDAGEM CONTRA UNDEFINED
                // Se a API não retornar nada, usamos o objeto padrão para não quebrar a tela
                const safeMetrics = response.data?.metrics || { total: 0, ended: 0, canceled: 0, avgTime: 0, agentsArray: [] };
                const safeChartData = response.data?.chartData || [];

                setMetrics(safeMetrics);
                setReportData(safeChartData);

            } catch (error) {
                console.error("Erro ao gerar relatório:", error);
                // Reseta se der erro
                setMetrics({ total: 0, ended: 0, canceled: 0, avgTime: 0, agentsArray: [] });
                setReportData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchReportData();
    }, [startDate, endDate, selectedAgent]);

    // Renderização Segura (Verifica se metrics existe antes de acessar propriedades)
    const safeMetrics = metrics || { total: 0, ended: 0, canceled: 0, avgTime: 0, agentsArray: [] };

    return (
        <div className="p-6 h-full overflow-y-auto bg-gray-50">
            <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center">
                        <Users className="mr-2 text-blue-600" /> Performance da Equipe
                    </h2>
                    <p className="text-sm text-gray-500">Métricas de atendimento por operador</p>
                </div>

                <div className="flex flex-wrap gap-2 items-center bg-white p-2 rounded-lg shadow-sm border border-gray-200">
                    <div className="flex items-center px-2 text-gray-500 text-sm">
                        <Filter className="w-4 h-4 mr-1" /> Filtros:
                    </div>
                    
                    <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-1.5 border rounded text-sm bg-gray-50"/>
                    <span className="text-gray-400">-</span>
                    <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-1.5 border rounded text-sm bg-gray-50"/>
                    
                    <select value={selectedAgent} onChange={e => setSelectedAgent(e.target.value)} className="p-1.5 border rounded text-sm bg-gray-50">
                        <option value="">Todos os Agentes</option>
                        {agentsList.map(u => (<option key={u._id || u.id} value={u._id || u.id}>{u.name}</option>))}
                    </select>

                    <div className="p-1.5 bg-blue-100 text-blue-700 rounded">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <TrendingUp className="w-4 h-4"/>}
                    </div>
                </div>
            </div>

            {/* CARTÕES DE MÉTRICAS (Usando safeMetrics) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"><p className="text-xs font-bold text-gray-400 uppercase">Total</p><p className="text-3xl font-bold text-gray-800 mt-1">{safeMetrics.total}</p></div>
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"><p className="text-xs font-bold text-gray-400 uppercase">Finalizados</p><p className="text-3xl font-bold text-green-600 mt-1">{safeMetrics.ended}</p></div>
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"><p className="text-xs font-bold text-gray-400 uppercase">Tempo Médio</p><div className="flex items-center mt-1"><Clock className="w-5 h-5 text-blue-500 mr-2" /><p className="text-2xl font-bold text-gray-800">{formatDuration(safeMetrics.avgTime)}</p></div></div>
                <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100"><p className="text-xs font-bold text-gray-400 uppercase">Cancelados</p><p className="text-3xl font-bold text-red-500 mt-1">{safeMetrics.canceled}</p></div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 mb-8">
                <h3 className="font-bold text-gray-700 mb-4">Volume Diário</h3>
                <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={reportData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="date" tick={{fontSize: 12}} />
                            <YAxis />
                            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}/>
                            <Legend />
                            <Bar dataKey="total" name="Total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="ended" name="Finalizados" fill="#10B981" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-4 border-b border-gray-100 bg-gray-50"><h3 className="font-bold text-gray-700">Detalhes por Operador</h3></div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b">
                            <tr><th className="px-6 py-3">Agente</th><th className="px-6 py-3 text-center">Total</th><th className="px-6 py-3 text-center">Finalizados</th><th className="px-6 py-3 text-center">Cancelados</th><th className="px-6 py-3 text-center">T.M.A</th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {safeMetrics.agentsArray && safeMetrics.agentsArray.length > 0 ? safeMetrics.agentsArray.map((agent) => (
                                <tr key={agent.name} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 font-medium text-gray-800">{agent.name}</td>
                                    <td className="px-6 py-4 text-center font-bold">{agent.total}</td>
                                    <td className="px-6 py-4 text-center text-green-600">{agent.ended}</td>
                                    <td className="px-6 py-4 text-center text-red-600">{agent.canceled}</td>
                                    <td className="px-6 py-4 text-center text-gray-600">{formatDuration(agent.tma)}</td>
                                </tr>
                            )) : <tr><td colSpan="5" className="px-6 py-8 text-center text-gray-400">Nenhum dado encontrado.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default RelatoriosScreen;